import { createFileRoute, Link } from "@tanstack/react-router";
import { MapPin } from "lucide-react";
import { serviceAreas } from "@/data/serviceAreas";
import { BUSINESS_ID, breadcrumbSchema, serviceSchema } from "@/lib/localBusinessSchema";
import heroImg from "@/assets/denver-service-area.jpg";
import apexTheaterRichImg from "@/assets/Apex_Theater.webp.asset.json";

export const Route = createFileRoute("/service-areas/")({
  head: () => ({
    meta: [
      { title: "Service Areas — Denver Metro Home Automation & AV | Apex AV" },
      { name: "description", content: "Home automation & AV installation across Denver, Aurora, Cherry Creek, Highlands Ranch, Castle Rock & the Front Range. Free local consultations." },
      { name: "keywords", content: "home automation Denver Metro, AV installer near me, smart home installer Aurora, home theater Cherry Creek, smart home Castle Rock, AV company Highlands Ranch, Boulder home automation, Parker AV installer, Castle Pines smart home, Front Range AV company" },
      { property: "og:title", content: "Service Areas — Denver Metro AV Installation" },
      { property: "og:description", content: "Custom home automation & AV installation across the Denver Metro Area & Front Range. Free local consultations." },
      { property: "og:url", content: "https://apexav.com/service-areas" },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "Service Areas — Denver Metro AV Installation" },
      { name: "twitter:description", content: "Custom home automation & AV installation across the Denver Metro Area & Front Range. Free local consultations." },
      { property: "og:image", content: `https://apexav.com${apexTheaterRichImg.url}` },
      { property: "og:image:alt", content: "Apex AV custom home theater installation in Denver, Colorado" },
      { name: "twitter:image", content: `https://apexav.com${apexTheaterRichImg.url}` },
    ],
    links: [{ rel: "canonical", href: "https://apexav.com/service-areas" }, { rel: "preload", as: "image", href: heroImg, fetchPriority: "high" } as never],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "CollectionPage",
              name: "Apex AV Service Areas — Denver Metro & Front Range",
              url: "https://apexav.com/service-areas",
              description: "Home automation and AV installation across Denver, Aurora, Cherry Creek, Highlands Ranch, Castle Rock, Boulder, and the Front Range.",
              breadcrumb: breadcrumbSchema([
                { name: "Home", url: "https://apexav.com/" },
                { name: "Service Areas", url: "https://apexav.com/service-areas" },
              ]),
              mainEntity: {
                "@type": "ItemList",
                itemListElement: serviceAreas.map((a, i) => ({
                  "@type": "ListItem",
                  position: i + 1,
                  name: `${a.name}, CO`,
                  url: `https://apexav.com/service-areas/${a.slug}`,
                })),
              },
            },
            {
              "@id": BUSINESS_ID,
              areaServed: serviceAreas.map((a) => ({ "@type": "City", name: `${a.name}, CO` })),
            },
            serviceSchema({
              name: "Home Automation & AV Installation — Denver Metro",
              description: "Custom home theater, smart home automation, whole-home audio, lighting, shading, security, and networking installation across the Denver Metro Area and Front Range.",
              url: "https://apexav.com/service-areas",
              serviceType: "Home Automation & AV Installation",
            }),
          ],
        }),
      },
    ],
  }),
  component: ServiceAreasPage,
});

const areasBySlug: Record<string, (typeof serviceAreas)[number] | undefined> = Object.fromEntries(
  serviceAreas.map((a) => [a.slug, a])
);

const areaGroups: { heading: string; blurb: string; slugs: string[] }[] = [
  {
    heading: "Denver & central neighborhoods",
    blurb:
      "Home automation and AV installation in Denver proper and the close-in west side — LoDo lofts, Cherry Creek estates, and Golden foothill homes.",
    slugs: ["denver", "cherry-creek", "englewood", "lakewood", "arvada", "golden"],
  },
  {
    heading: "South Metro & Douglas County",
    blurb:
      "Smart home and home theater installation across the south suburbs, from Highlands Ranch and Centennial down through Castle Rock, Castle Pines, and Franktown acreage.",
    slugs: ["highlands-ranch", "littleton", "centennial", "parker", "castle-rock", "castle-pines", "franktown"],
  },
  {
    heading: "North & east metro plus Boulder",
    blurb:
      "AV and networking installs across Aurora, the northern suburbs, and Boulder County — new-build prewire and retrofit work alike.",
    slugs: ["aurora", "thornton", "westminster", "broomfield", "boulder"],
  },
];


function ServiceAreasPage() {
  return (
    <>
    <section className="relative overflow-hidden border-b border-border">
      <div className="absolute inset-0">
        <img src={heroImg} alt="Denver Colorado skyline serving Cherry Creek, Highlands Ranch, Castle Rock, Castle Pines, Greenwood Village smart home installations" className="h-full w-full object-cover opacity-40" width={1920} height={1080} loading="eager" fetchPriority="high" decoding="async" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-background/80 to-background" />
      </div>
      <div className="relative container mx-auto px-6 py-24 md:py-32">
        <div className="max-w-3xl">
          <p className="text-sm font-semibold text-brand-text uppercase tracking-wider">Service Areas</p>
          <h1 className="mt-3 font-display text-5xl md:text-6xl font-bold">Proudly serving the entire Denver Metro.</h1>
          <p className="mt-6 text-lg text-muted-foreground">
            From downtown Denver to Castle Rock, Boulder to Parker — Apex AV designs, installs, and supports home automation and AV systems across every Front Range community.
          </p>
        </div>
      </div>
    </section>

    <section className="container mx-auto px-6 py-12">
      <div className="max-w-3xl">
        <h2 className="font-display text-3xl md:text-4xl font-bold">Coverage across the Front Range</h2>
        <p className="mt-4 text-muted-foreground">
          Apex AV works throughout the Denver metro and out along the Front Range corridor, from close-in neighborhoods like Cherry Creek and Golden to south metro communities such as Highlands Ranch, Castle Rock, and Castle Pines, and north to Boulder, Broomfield, and Thornton. Because our crews are based centrally, most homes in these areas are reachable for both the initial consultation and ongoing service without long travel delays factoring into scheduling.
        </p>
        <h2 className="mt-10 font-display text-3xl md:text-4xl font-bold">Travel &amp; scheduling</h2>
        <p className="mt-4 text-muted-foreground">
          We schedule consultations and installs by drive time and project scope rather than a fixed radius, so acreage properties in Franktown or Parker get the same attention as a downtown Denver condo. For larger installs, we typically block full days on-site to minimize back-and-forth, and we coordinate around Colorado's weather swings when work involves exterior speakers, outdoor TVs, or antenna and satellite equipment.
        </p>
        <h2 className="mt-10 font-display text-3xl md:text-4xl font-bold">Local construction differences</h2>
        <p className="mt-4 text-muted-foreground">
          Front Range homes vary a lot by build era and location — older Denver bungalows with plaster walls need different cable-fishing approaches than newer Highlands Ranch or Castle Rock construction with open attics, and mountain-adjacent homes in Golden or Boulder County often call for extra planning around stucco, stone, or log construction. We also see plenty of active new construction across Douglas County and the south metro, where getting on-site during framing for prewire saves homeowners from cutting into finished walls later.
        </p>
      </div>
    </section>
    {areaGroups.map((group) => (
      <section key={group.heading} className="container mx-auto px-6 py-12">
        <h2 className="font-display text-3xl md:text-4xl font-bold">{group.heading}</h2>
        <p className="mt-3 max-w-2xl text-muted-foreground">{group.blurb}</p>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {group.slugs.map((slug) => {
            const a = areasBySlug[slug];
            if (!a) return null;
            return (
              <Link
                key={a.slug}
                to="/service-areas/$slug"
                params={{ slug: a.slug }}
                className="group rounded-xl border border-border bg-card p-6 shadow-card hover:border-brand/50 transition"
              >
                <div className="flex items-start justify-between">
                  <div className="h-10 w-10 rounded-lg bg-gradient-accent flex items-center justify-center">
                    <MapPin className="h-5 w-5 text-accent-foreground" />
                  </div>
                  <span className="text-xs text-muted-foreground">{a.zip}</span>
                </div>
                <h3 className="mt-5 text-xl font-display font-bold group-hover:text-gradient-brand">{a.name}</h3>
                <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{a.blurb}</p>
              </Link>
            );
          })}
        </div>
      </section>
    ))}

    </>
  );
}
